import { useState, useEffect } from 'react'
import { useI18n } from '@/lib/i18n'
import { useAppStore } from '@/store'
import { useSearchParams } from 'react-router-dom'
import EventDefaults from '../components/EventDefaults'
import { Cpu, HardDrive, Info, Activity, Monitor, LogOut, Globe, Download, Laptop, Server, Wifi, WifiOff, FolderOpen, KeyRound, Copy, Trash2, Check, RefreshCw } from 'lucide-react'

interface DiskInfo {
  size: string
  used: string
  available: string
  percent: string
}

interface SystemStatus {
  name: string
  version: string
  os: { platform: string; release: string; uptime: number; loadavg: number[] }
  memory: { total: number; free: number; used: number }
  disk: DiskInfo
  nas: { connected: boolean; writable: boolean; disk: DiskInfo | null }
  storage: { active: 'nas' | 'local'; capturesPath: string }
}

interface ElectronSettings {
  url: string
  mode: 'windowed' | 'fullscreen' | 'kiosk'
  autoStart: boolean
  preventSleep: boolean
  apiKey?: string
  hqSaveLocation?: string
}

interface ApiKey {
  id: string
  name: string
  key_prefix: string
  created_at: string
  last_used_at: string | null
}

export default function SettingsPage() {
  const { t } = useI18n()
  const [settingsParams, setSettingsParams] = useSearchParams()
  const showDefaults = settingsParams.get('tab') !== 'system'
  const locale = useAppStore((s) => s.locale)
  const setLocale = useAppStore((s) => s.setLocale)
  const darkMode = useAppStore((s) => s.darkMode)
  const setDarkMode = useAppStore((s) => s.setDarkMode)
  const [status, setStatus] = useState<SystemStatus | null>(null)
  const [forceUpdate, setForceUpdate] = useState(false)

  const [electronSettings, setElectronSettings] = useState<ElectronSettings | null>(null)
  const [hqOriginalsDir, setHqOriginalsDir] = useState<string | null>(null)
  const [clientVersion, setClientVersion] = useState<string | null>(null)

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [newKeyName, setNewKeyName] = useState('')
  const [generatingKey, setGeneratingKey] = useState(false)
  const [revealedKey, setRevealedKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const loadApiKeys = () => {
    fetch('/api/auth/api-keys')
      .then(r => r.json())
      .then(data => setApiKeys(data.keys || []))
      .catch(console.error)
  }

  useEffect(() => {
    fetch('/api/system/status')
      .then(r => r.json())
      .then(setStatus)
      .catch(console.error)
    fetch('/api/system/update-policy')
      .then(r => r.json())
      .then(data => setForceUpdate(!!data.forceUpdate))
      .catch(console.error)
    loadApiKeys()
  }, [])

  const updateForceUpdatePolicy = async (value: boolean) => {
    setForceUpdate(value)
    try {
      await fetch('/api/system/update-policy', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ forceUpdate: value }),
      })
    } catch (err) {
      console.error('Failed to update policy:', err)
    }
  }

  useEffect(() => {
    if (window.electronAPI) {
      window.electronAPI.getSettings()
        .then(setElectronSettings)
        .catch(console.error)
      window.electronAPI.getHQOriginalsDir()
        .then(setHqOriginalsDir)
        .catch(console.error)
      window.electronAPI.getAppVersion()
        .then(setClientVersion)
        .catch(console.error)
    }
  }, [])

  const updateElectronSetting = async (key: keyof ElectronSettings, value: any) => {
    if (!electronSettings || !window.electronAPI) return
    const updated = { ...electronSettings, [key]: value }
    setElectronSettings(updated)
    await window.electronAPI.saveSettings(updated)
  }

  const handleGenerateKey = async () => {
    if (!newKeyName.trim() || generatingKey) return
    setGeneratingKey(true)
    try {
      const res = await fetch('/api/auth/api-keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newKeyName.trim() }),
      })
      if (res.ok) {
        const data = await res.json()
        setRevealedKey(data.key)
        setNewKeyName('')
        loadApiKeys()
      } else {
        alert('Failed to generate application password.')
      }
    } catch (err) {
      console.error(err)
      alert('Failed to generate application password.')
    } finally {
      setGeneratingKey(false)
    }
  }

  const handleRevokeKey = async (id: string) => {
    if (!confirm('Revoke this application password? Any device using it will need a new one.')) return
    await fetch(`/api/auth/api-keys/${id}`, { method: 'DELETE' })
    loadApiKeys()
  }

  const handleCopyKey = () => {
    if (!revealedKey) return
    navigator.clipboard.writeText(revealedKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleExitApp = () => {
    if (window.electronAPI) {
      window.electronAPI.exitApp()
    }
  }

  const formatBytes = (bytes: number) => {
    const gb = bytes / (1024 * 1024 * 1024)
    return gb.toFixed(2) + ' GB'
  }

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / (24 * 3600))
    const hrs = Math.floor((seconds % (24 * 3600)) / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    return `${days}d ${hrs}h ${mins}m`
  }

  return (
    <div className="p-6 max-w-4xl space-y-6">
      <h1 className="text-2xl font-bold">{t('nav.settings')}</h1>

      <div className="flex gap-2 border-b border-border pb-3" role="tablist" aria-label={t('nav.settings')}>
        <button role="tab" aria-selected={showDefaults} onClick={() => setSettingsParams({})} className={`rounded-lg px-4 py-2 text-sm font-medium ${showDefaults ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}`}>{t('inheritance.defaultsTitle')}</button>
        <button role="tab" aria-selected={!showDefaults} onClick={() => setSettingsParams({ tab: 'system' })} className={`rounded-lg px-4 py-2 text-sm font-medium ${!showDefaults ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}`}>{t('inheritance.systemTab')}</button>
      </div>
      {showDefaults ? <EventDefaults /> : <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-6">
          <div className="space-y-4 rounded-xl border border-border bg-card/50 p-5">
            <h2 className="flex items-center gap-2 font-semibold">
              <Activity className="h-4 w-4 text-primary" />
              {t('common.appearance')}
            </h2>
            <label className="flex items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 cursor-pointer hover:bg-muted transition-colors">
              <input type="checkbox" checked={darkMode} onChange={(e) => setDarkMode(e.target.checked)} className="h-4 w-4" />
              <div className="text-sm font-medium">{t('common.darkMode')}</div>
            </label>
          </div>

          <div className="space-y-4 rounded-xl border border-border bg-card/50 p-5">
            <h2 className="font-semibold">{t('common.language')}</h2>
            <select value={locale} onChange={(e) => setLocale(e.target.value as 'en' | 'nl')} className="w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm">
              <option value="en">English</option>
              <option value="nl">Nederlands</option>
            </select>
          </div>

          {electronSettings && (
            <div className="space-y-4 rounded-xl border border-border bg-card/50 p-5">
              <h2 className="flex items-center justify-between font-semibold">
                <span className="flex items-center gap-2">
                  <Monitor className="h-4 w-4 text-primary" />
                  Desktop Kiosk Settings
                </span>
                {clientVersion && (
                  <span className="text-xs font-mono font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                    v{clientVersion}
                  </span>
                )}
              </h2>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                    Kiosk Target URL
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <input 
                      type="text" 
                      value={electronSettings.url} 
                      onChange={(e) => setElectronSettings({ ...electronSettings, url: e.target.value })}
                      onBlur={() => updateElectronSetting('url', electronSettings.url)}
                      className="w-full rounded-lg border border-input bg-background pl-9 pr-4 py-2.5 text-sm"
                      placeholder="https://posebox.nl/"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                    Display Mode
                  </label>
                  <select 
                    value={electronSettings.mode} 
                    onChange={(e) => updateElectronSetting('mode', e.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm"
                  >
                    <option value="windowed">Windowed (1280x800)</option>
                    <option value="fullscreen">Fullscreen</option>
                    <option value="kiosk">Kiosk Mode (Locked)</option>
                  </select>
                </div>

                <label className="flex items-center justify-between rounded-lg border border-border bg-background px-4 py-3 cursor-pointer hover:bg-muted transition-colors">
                  <div className="flex flex-col gap-0.5">
                    <div className="text-sm font-medium">Start Automatically</div>
                    <div className="text-xs text-muted-foreground">Launch kiosk app when system boots</div>
                  </div>
                  <input 
                    type="checkbox" 
                    checked={electronSettings.autoStart} 
                    onChange={(e) => updateElectronSetting('autoStart', e.target.checked)} 
                    className="h-4 w-4 text-primary focus:ring-primary rounded" 
                  />
                </label>

                <label className="flex items-center justify-between rounded-lg border border-border bg-background px-4 py-3 cursor-pointer hover:bg-muted transition-colors">
                  <div className="flex flex-col gap-0.5">
                    <div className="text-sm font-medium">Prevent Display Sleep</div>
                    <div className="text-xs text-muted-foreground">Keep screen awake during operation</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={electronSettings.preventSleep}
                    onChange={(e) => updateElectronSetting('preventSleep', e.target.checked)}
                    className="h-4 w-4 text-primary focus:ring-primary rounded"
                  />
                </label>

                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                    Application Password
                  </label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={electronSettings.apiKey || ''}
                      onChange={(e) => setElectronSettings({ ...electronSettings, apiKey: e.target.value })}
                      onBlur={() => updateElectronSetting('apiKey', electronSettings.apiKey || '')}
                      className="w-full rounded-lg border border-input bg-background pl-9 pr-4 py-2.5 text-sm font-mono"
                      placeholder="pb_••••••••••••••••••••••••"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Paste a key from the Application Passwords card below to sign in automatically on this device.</p>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                    Local HQ Save Location
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={electronSettings.hqSaveLocation || ''}
                      onChange={(e) => setElectronSettings({ ...electronSettings, hqSaveLocation: e.target.value })}
                      onBlur={() => updateElectronSetting('hqSaveLocation', electronSettings.hqSaveLocation || '')}
                      className="flex-1 rounded-lg border border-input bg-background px-4 py-2.5 text-sm font-mono"
                      placeholder="C:\PoseBox"
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        const picked = await window.electronAPI?.pickHQFolder()
                        if (picked) updateElectronSetting('hqSaveLocation', picked)
                      }}
                      className="shrink-0 rounded-lg border border-border bg-background px-4 py-2.5 text-sm font-semibold hover:bg-muted transition-colors"
                    >
                      Browse
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">Where full-quality originals are saved on this machine, organized as &lt;location&gt;\&lt;event name&gt;\&lt;session&gt;. Leave blank to use the default (C:\PoseBox).</p>
                </div>

                <button
                  type="button"
                  onClick={handleExitApp}
                  className="flex items-center justify-center gap-2 w-full rounded-lg border border-red-500/20 bg-red-500/5 hover:bg-red-500/10 text-red-500 px-4 py-2.5 text-sm font-semibold transition-colors mt-2"
                >
                  <LogOut className="h-4 w-4" />
                  Exit Kiosk Application
                </button>
              </div>
            </div>
          )}

          {electronSettings && (
            <div className="space-y-4 rounded-xl border border-border bg-card/50 p-5">
              <h2 className="flex items-center gap-2 font-semibold">
                <FolderOpen className="h-4 w-4 text-primary" />
                Local HQ Archive
              </h2>
              <p className="text-xs text-muted-foreground text-left">
                Every photo, print layout, and video is also saved at full quality directly to this machine's disk, separate from whatever gets uploaded to the server, so a slow connection or Data Saver mode never costs you the original.
              </p>
              {hqOriginalsDir && (
                <div className="rounded-lg bg-background p-3 border border-border">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Saved To</div>
                  <div className="text-xs font-mono break-all">{hqOriginalsDir}</div>
                </div>
              )}
              <button
                type="button"
                onClick={() => window.electronAPI?.openHQOriginalsFolder()}
                className="flex items-center justify-center gap-2 w-full rounded-lg border border-border bg-background hover:bg-muted px-4 py-2.5 text-sm font-semibold transition-colors"
              >
                <FolderOpen className="h-4 w-4" />
                Open Folder
              </button>
            </div>
          )}

          <div className="space-y-4 rounded-xl border border-border bg-card/50 p-5">
            <h2 className="flex items-center gap-2 font-semibold">
              <KeyRound className="h-4 w-4 text-primary" />
              Application Passwords
            </h2>
            <p className="text-xs text-muted-foreground text-left">
              Generate a key to sign in a kiosk device automatically, without giving it your real account password. Revoke it any time without changing your login.
            </p>

            {revealedKey && (
              <div className="space-y-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
                <div className="text-xs font-semibold text-emerald-500 uppercase tracking-wider">
                  Copy this now. It won't be shown again
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded-md bg-background px-3 py-2 text-xs font-mono break-all border border-border">{revealedKey}</code>
                  <button
                    type="button"
                    onClick={handleCopyKey}
                    className="shrink-0 rounded-md bg-background border border-border p-2 hover:bg-muted transition-colors"
                    title="Copy"
                  >
                    {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setRevealedKey(null)}
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                >
                  Done, dismiss
                </button>
              </div>
            )}

            <div className="flex gap-2">
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleGenerateKey()}
                placeholder="e.g. Front Door Kiosk"
                className="flex-1 rounded-lg border border-input bg-background px-4 py-2.5 text-sm"
              />
              <button
                type="button"
                onClick={handleGenerateKey}
                disabled={!newKeyName.trim() || generatingKey}
                className="rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors whitespace-nowrap"
              >
                {generatingKey ? 'Generating...' : 'Generate'}
              </button>
            </div>

            {apiKeys.length > 0 && (
              <div className="space-y-2">
                {apiKeys.map((k) => (
                  <div key={k.id} className="flex items-center justify-between rounded-lg border border-border bg-background px-4 py-2.5">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{k.name}</div>
                      <div className="text-xs text-muted-foreground font-mono">
                        {k.key_prefix}••••••••
                        {k.last_used_at ? ` · last used ${new Date(k.last_used_at).toLocaleDateString()}` : ' · never used'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRevokeKey(k.id)}
                      className="shrink-0 rounded-md p-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
                      title="Revoke"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-4 rounded-xl border border-border bg-card/50 p-5">
            <h2 className="flex items-center gap-2 font-semibold">
              <RefreshCw className="h-4 w-4 text-primary" />
              Kiosk Auto-Update Policy
            </h2>
            <label className="flex items-center justify-between rounded-lg border border-border bg-background px-4 py-3 cursor-pointer hover:bg-muted transition-colors">
              <div className="flex flex-col gap-0.5">
                <div className="text-sm font-medium">Force Update (Mandatory)</div>
                <div className="text-xs text-muted-foreground">
                  {forceUpdate
                    ? 'Kiosk devices download and install updates automatically, with no way to defer.'
                    : "Kiosk devices get a dismissible prompt. The operator chooses when to update."}
                </div>
              </div>
              <input
                type="checkbox"
                checked={forceUpdate}
                onChange={(e) => updateForceUpdatePolicy(e.target.checked)}
                className="h-4 w-4 text-primary focus:ring-primary rounded shrink-0 ml-4"
              />
            </label>
            <p className="text-xs text-muted-foreground">
              Applies to every kiosk device pointed at this server. This isn't per-event. Off by default.
            </p>
          </div>

          <div className="space-y-4 rounded-xl border border-border bg-card/50 p-5">
            <h2 className="flex items-center justify-between font-semibold">
              <span className="flex items-center gap-2">
                <Download className="h-4 w-4 text-primary" />
                Kiosk Client Downloads
              </span>
              {status && (
                <span className="text-xs font-mono font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                  v{status.version}
                </span>
              )}
            </h2>
            <p className="text-xs text-muted-foreground text-left">
              Download the standalone desktop kiosk client to run the photobooth in locked fullscreen, prevent system sleep, and configure automatic startup on boot. The Windows installer auto-updates itself from here on, you shouldn't need to redownload it manually again.
            </p>
            <div className="grid gap-2">
              <a
                href={`/downloads/PoseBox%20Kiosk-${status?.version}-win-setup.exe`}
                download
                className="flex items-center justify-between rounded-lg border border-border bg-background px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left"
              >
                <div className="flex items-center gap-2">
                  <Laptop className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium text-foreground">Windows Installer (.exe, auto-updates)</span>
                </div>
                <Download className="h-4 w-4 text-muted-foreground" />
              </a>

              <a
                href={`/downloads/PoseBox%20Kiosk-${status?.version}.AppImage`}
                download
                className="flex items-center justify-between rounded-lg border border-border bg-background px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded text-amber-500 font-semibold">AppImage</span>
                  <span className="font-medium text-foreground">Linux Client (.AppImage)</span>
                </div>
                <Download className="h-4 w-4 text-muted-foreground" />
              </a>

              <a
                href={`/downloads/posebox_${status?.version}_amd64.deb`}
                download
                className="flex items-center justify-between rounded-lg border border-border bg-background px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded text-emerald-500 font-semibold font-sans">deb</span>
                  <span className="font-medium text-foreground">Linux Debian Package (.deb)</span>
                </div>
                <Download className="h-4 w-4 text-muted-foreground" />
              </a>
            </div>
            <p className="text-xs text-muted-foreground text-left">
              Zorin OS / Ubuntu / Debian: download the .deb and double-click it to install, or run{' '}
              <span className="font-mono bg-muted px-1.5 py-0.5 rounded text-foreground">sudo apt install ./posebox_{status?.version}_amd64.deb</span>.
              The Linux client does not auto-update. Reinstall the .deb after a new version.
            </p>
          </div>
        </div>

        {status && (
          <div className="space-y-4 rounded-xl border border-border bg-card/50 p-5">
            <h2 className="flex items-center gap-2 font-semibold">
              <Info className="h-4 w-4 text-primary" />
              {t('settings.systemInfo')}
            </h2>
            <div className="grid gap-4">
              <div className="flex items-center justify-between rounded-lg bg-background p-3 border border-border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Cpu className="h-4 w-4" /> {t('settings.os')}
                </div>
                <div className="text-sm font-mono">{status.os.platform} {status.os.release}</div>
              </div>

              <div className="flex items-center justify-between rounded-lg bg-background p-3 border border-border">
                <div className="text-sm text-muted-foreground">{t('settings.software')}</div>
                <div className="text-sm font-semibold text-primary">{status.name}</div>
              </div>

              <div className="flex items-center justify-between rounded-lg bg-background p-3 border border-border">
                <div className="text-sm text-muted-foreground">{t('settings.version')}</div>
                <div className="text-sm font-mono">v{status.version}</div>
              </div>

              <div className="flex items-center justify-between rounded-lg bg-background p-3 border border-border">
                <div className="text-sm text-muted-foreground">{t('settings.uptime')}</div>
                <div className="text-sm font-mono">{formatUptime(status.os.uptime)}</div>
              </div>

              <div className="space-y-2 rounded-lg bg-background p-3 border border-border">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Activity className="h-4 w-4" /> {t('settings.memory')}
                  </div>
                  <div className="font-mono">{formatBytes(status.memory.used)} / {formatBytes(status.memory.total)}</div>
                </div>
                <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-500" 
                    style={{ width: `${(status.memory.used / status.memory.total) * 100}%` }}
                  />
                </div>
              </div>

              <div className="space-y-2 rounded-lg bg-background p-3 border border-border">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <HardDrive className="h-4 w-4" /> Local Disk
                  </div>
                  <div className="font-mono">{status.disk.used} / {status.disk.size}</div>
                </div>
                <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 transition-all duration-500" 
                    style={{ width: status.disk.percent }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Storage / NAS Status */}
        {status && (
          <div className="space-y-4 rounded-xl border border-border bg-card/50 p-5 md:col-span-2">
            <h2 className="flex items-center gap-2 font-semibold">
              <Server className="h-4 w-4 text-primary" />
              Storage
            </h2>
            <div className="grid gap-4 md:grid-cols-2">
              {/* NAS Connection Status */}
              <div className="space-y-3 rounded-lg bg-background p-4 border border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    {status.nas.connected ? (
                      <Wifi className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <WifiOff className="h-4 w-4 text-red-500" />
                    )}
                    External Storage (NAS)
                  </div>
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    status.nas.connected 
                      ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' 
                      : 'bg-red-500/15 text-red-400 border border-red-500/30'
                  }`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${status.nas.connected ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
                    {status.nas.connected ? 'Connected' : 'Disconnected'}
                  </span>
                </div>

                {status.nas.connected && status.nas.disk && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">NAS Disk</span>
                      <span className="font-mono">{status.nas.disk.used} / {status.nas.disk.size}</span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                      <div 
                        className="h-full bg-primary transition-all duration-500" 
                        style={{ width: status.nas.disk.percent }}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {status.nas.disk.available} available
                    </div>
                  </div>
                )}

                {!status.nas.connected && (
                  <p className="text-xs text-red-400/80">
                    NAS is unreachable. Photos are being saved to local storage as a fallback.
                  </p>
                )}
              </div>

              {/* Active Storage Info */}
              <div className="space-y-3 rounded-lg bg-background p-4 border border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <HardDrive className="h-4 w-4 text-primary" />
                    Active Storage
                  </div>
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    status.storage.active === 'nas'
                      ? 'bg-primary/15 text-primary border border-primary/30'
                      : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                  }`}>
                    {status.storage.active === 'nas' ? 'NAS' : 'Local'}
                  </span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Local Disk</span>
                    <span className="font-mono">{status.disk.used} / {status.disk.size}</span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div 
                      className="h-full bg-emerald-500 transition-all duration-500" 
                      style={{ width: status.disk.percent }}
                    />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {status.disk.available} available
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>}
    </div>
  )
}
